import * as React from "react";

import dayjs from "dayjs";
import _ from "lodash";

import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";

import { ChartIds } from "pro-features/components/Chart/src/components/annotations/Helpers/ChartIdsHelper";
import { i18nFilter } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import { PopTooltip } from "app/pages/website-analysis/TrafficAndEngagement/ChartTooltips/ChartTooltips";
import { CompareModeTooltip } from "app/pages/website-analysis/TrafficAndEngagement/ChartTooltips/CommonChartTooltip";
import { GenericGraph } from "app/pages/website-analysis/TrafficAndEngagement/Components/GenericGraph";
import {
    tooltipPositioner,
    POPAndCompareTooltipPositioner,
} from "app/pages/website-analysis/TrafficAndEngagement/MD/chartConfig";
import { parsePOPCompareModeDataWithMTD } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/ParseServerData";
import {
    chartGranularityItemClickArea,
    dateFormatter,
    getChartType,
    getGetExcel,
    isTimeGranularityDisabledOnlyMonth,
    minVisitsAbbrFormatter,
    neverStacking,
    OVERVIEW_EXCEL_ENDPOINT,
    POP_COMPARE_EXCEL_END_POINT,
    UNIQUE_USERS_EXCEL_ENDPOINT,
} from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import {
    getTooltipHeader as getDefaultTooltipHeader,
    getTooltipHeaderElement,
} from "app/UtilitiesAndConstants/UtilitiesComponents/chartTooltipHeaderWithMTDSupport";
import { addPartialDataZones } from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";

const ENDPOINT_METRIC = "UniqueUsers";

// Custom parseData for UniqueUsers with special weekly partial handling
const parseUniqueUsersData = ({ isSingleMode, sites, chosenSite, data, chartType }) => {
    const metricData = data.Data;
    if (isSingleMode) {
        return addPartialDataZones(
            sites.map((site) => {
                let key,
                    siteForAccessingData = chosenSite;
                if (site.isCategory) {
                    siteForAccessingData = site.alias;
                    key = Object.keys(metricData[siteForAccessingData])[0];
                } else {
                    key = metricData[siteForAccessingData][site.name] ? site.name : site.alias;
                }
                const itemsData = Array.isArray(metricData[siteForAccessingData][key])
                    ? metricData[siteForAccessingData][key][0]
                    : metricData[siteForAccessingData][key];

                const sortedItemData = _.sortBy(itemsData, "Key");
                return {
                    name: site.name,
                    color: site.color,
                    isSelected: site.isSelected,
                    data: sortedItemData.map((item) => ({
                        x: dayjs.utc(item.Key).valueOf(),
                        y: item.Value,
                        isPartial: false,
                    })),
                    stack: chosenSite,
                };
            }),
            { chartType },
        );
    } else {
        return addPartialDataZones(
            sites.map((site) => ({
                name: site.name,
                color: site.color,
                isSelected: site.isSelected,
                data: Object.values(metricData[site.name] || metricData[site.alias])[0][0].map((item, idx, arr) => ({
                    x: dayjs.utc(item.Key).valueOf(),
                    y: item.Value,
                    isPartial: false,
                })),
                stack: site.name,
            })),
            { chartType },
        );
    }
};

// Custom tooltip header for UniqueUsers that shows full week range when week is complete
const getUniqueUsersTooltipHeader = (
    timeGranularity: GranularityNames,
    points: readonly any[],
    lastSupportedDate?: string,
    asText?: boolean,
    alertMessage?: string,
): string | React.ReactElement => {
    if (timeGranularity !== GranularityNames.WEEKLY) {
        // For non-weekly, use default behavior
        return getDefaultTooltipHeader(timeGranularity, points, lastSupportedDate, asText, alertMessage) as any;
    }
    const weekStartMoment = dayjs.utc(points[0].x);
    const toDate = weekStartMoment.add(6, "days");
    const fromDateText = weekStartMoment.format(shortMonthDayAndYear);
    const toDateText = toDate.format(shortMonthDayAndYear);

    const text = i18nFilter()("graph.tooltip.from.date.to.date", {
        fromDate: fromDateText,
        toDate: toDateText,
    });
    if (asText) {
        return text;
    }

    // Return the same structure as getTooltipHeaderElement
    // For alertMessage, get the bell content from default tooltip (but with a dummy granularity to avoid recursion)
    const alertContent = alertMessage
        ? (() => {
              const defaultResult = getTooltipHeaderElement(
                  GranularityNames.DAILY, // Use daily to avoid weekly logic recursion
                  points[0].x,
                  lastSupportedDate,
                  false,
                  alertMessage,
              );
              if (React.isValidElement(defaultResult)) {
                  const props = defaultResult.props as { children?: React.ReactNode };
                  if (props.children) {
                      const children = React.Children.toArray(props.children);
                      return children.find(
                          (child: any) =>
                              React.isValidElement(child) &&
                              child.type &&
                              typeof child.type === "function" &&
                              child.type.name === "BellContentWrapper",
                      );
                  }
              }
              return null;
          })()
        : null;

    return (
        <>
            <span>{text}</span>
            {alertContent}
        </>
    );
};

export const UniqueUsers = (props) => {
    const {
        name,
        getChartTooltipHeaders,
        meta: { isSingleMode, isPOP, isNonTotalWebSource, isCategoryActive },
    } = props;
    let metricData;
    if (!isPOP) {
        const addToDashboardName = "UniqueUsers";
        const yAxisLabelsFormatter = ({ value }) => minVisitsAbbrFilter()(value);
        const getExcelLink = (apiParams) => {
            if (apiParams?.timeGranularity === GranularityNames.WEEKLY) {
                return getGetExcel(UNIQUE_USERS_EXCEL_ENDPOINT)(apiParams);
            }
            return getGetExcel(OVERVIEW_EXCEL_ENDPOINT)(apiParams);
        };
        const isTimeGranularityDisabledUnique = ({ duration, title, isGa }) =>
            (duration === "28d" && (title === "M" || title === "W")) || (isGa && title === "D");
        const chartTooltip = (props) => (
            <CompareModeTooltip
                {...props}
                tableHeaders={getChartTooltipHeaders(props.timeGranularity)}
                xFormatter={dateFormatter}
                yFormatter={minVisitsAbbrFormatter}
                totalAggregate={isCategoryActive ? null : (...values) => values.reduce((acc, v) => acc + v, 0)}
                getTooltipHeader={getUniqueUsersTooltipHeader}
            />
        );
        const getGraphData = parseUniqueUsersData;
        metricData = {
            isTimeGranularityDisabled: isTimeGranularityDisabledUnique,
            getChartType,
            yAxisLabelsFormatter,
            getExcelLink,
            addToDashboardName,
            chartTooltip,
            getGraphData,
            chartGranularityItemClick:
                !isCategoryActive && isSingleMode && !isNonTotalWebSource ? chartGranularityItemClickArea : undefined,
            tooltipPositioner,
        };
    } else {
        const yAxisLabelsFormatter = ({ value }) => percentageSignFilter()(value, 0);
        const getExcelLink = (apiParams) => {
            if (apiParams?.timeGranularity === GranularityNames.WEEKLY) {
                return getGetExcel(UNIQUE_USERS_EXCEL_ENDPOINT)(apiParams);
            }
            return getGetExcel(POP_COMPARE_EXCEL_END_POINT)(apiParams);
        };
        const getGraphData = parsePOPCompareModeDataWithMTD;
        const chartTooltip = (props) => (
            <PopTooltip
                pointsData={props.points}
                xFormatter={dateFormatter}
                yFormatter={minVisitsAbbrFormatter}
            />
        );
        const popApiResponseName = "UniqueUsers";
        metricData = {
            isTimeGranularityDisabled: isTimeGranularityDisabledOnlyMonth,
            getChartType,
            yAxisLabelsFormatter,
            getExcelLink,
            getGraphData,
            chartTooltip,
            popApiResponseName,
            isStacking: neverStacking,
            hideLegendsData: true,
            tooltipPositioner: POPAndCompareTooltipPositioner,
        };
    }
    metricData.endpointMetric = ENDPOINT_METRIC;
    metricData.name = name;
    return (
        <GenericGraph
            metric={metricData}
            {...props}
            chartIdForAnnotations={`${ChartIds["TrafficAndEngagementUniqueUsers"]}`}
        />
    );
};
